import ProgressBar from "@/components/ProgressBar";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import datashipsLogoDark from "@/assets/svgs/dataships-logo-dark.svg";
import datashipsLogoLight from "@/assets/svgs/dataships-logo.svg";
import { usePostLogout } from "@/requests/hooks/user";
import { Button } from "@/components/Button";
import { useQueryClient } from "@tanstack/react-query";
import { tv } from "tailwind-variants";
import useIsMobile from "@/utils/useIsMobile";
import { collectOnboardingRoutes } from "@/navigation/onboardingRouter";
import { logoutSuccessCallback } from "@/requests/callbacks/user";

const styles = tv({
  slots: {
    base: "fixed left-0 right-0 top-0 z-30 h-onboarding-header-height-mobile flex-grow md:h-onboarding-header-height md:bg-transparent ",
    container:
      "flex w-full max-w-lg flex-row items-center justify-between bg-transparent p-20 md:px-130 md:py-50 2xl:mx-auto",
    logo: "w-120 md:w-[180px]",
  },
});

interface IProps {
  overrideDarkButtons?: boolean;
}

const OnboardingHeader = ({ overrideDarkButtons }: IProps) => {
  const queryClient = useQueryClient();
  const {
    mutate: logout,
    isPending,
    isSuccess: isLogoutSuccess,
  } = usePostLogout();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [hasDarkBackground, setHasDarkBackground] = useState(false);
  const [progress, setProgress] = useState(50);
  const { t } = useTranslation();
  const { base, container, logo } = styles();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (
      pathname.includes("/signup") ||
      pathname.includes("/auth/email-") ||
      pathname.includes("/profile")
    )
      return setHasDarkBackground(true);

    return setHasDarkBackground(false);
  }, []);

  useEffect(() => {
    calculateProgressPercentage();
  }, [pathname]);

  useEffect(() => {
    if (isLogoutSuccess) logoutSuccessCallback(queryClient, navigate);
  }, [isLogoutSuccess]);

  const calculateProgressPercentage = () => {
    const currentScreenIndex = collectOnboardingRoutes.findIndex(({ path }) =>
      pathname.includes(path),
    );
    const currentProgress =
      ((currentScreenIndex + 1) / collectOnboardingRoutes.length) * 100;

    setProgress(currentProgress);
  };

  const goToLogIn = () => {
    navigate("/auth/login");
  };

  const handleOnClickLogout = () => {
    logout();
  };

  const getBackgroundColor = () => {
    if (
      location.pathname.includes("/auth/email-") ||
      location.pathname.includes("/profile")
    )
      return "bg-transparent";

    if (pathname.includes("/signup")) return "bg-primary-dark";

    return "bg-gray-light";
  };

  const getLogoutButtonVariant = () => {
    if (typeof overrideDarkButtons === "boolean" && !!overrideDarkButtons)
      return "secondary-outline";

    return (hasDarkBackground && isMobile) ||
      pathname.includes("/profile") ||
      pathname.includes("/auth/email-verify")
      ? "white-outline"
      : "secondary-outline";
  };

  const renderProgressBar = () => {
    if (
      !location.pathname.includes("/auth/email-") &&
      !location.pathname.includes("/profile") &&
      !location.pathname.includes("/onboarding/comply/signup")
    )
      return <ProgressBar value={progress} />;
  };

  const renderTrailingButton = () => {
    if (
      pathname.includes("/email-confirm") ||
      pathname.includes("/email-verification-required") ||
      pathname.includes("/setup-complete")
    )
      return null;

    if (pathname.includes("/signup"))
      return (
        <Button
          variant={
            hasDarkBackground && isMobile
              ? "white-outline"
              : "secondary-outline"
          }
          onClick={goToLogIn}
          width="sm"
          testId="onboarding-header-trailing-button">
          {t("layouts.onboardingHeader.logIn")}
        </Button>
      );

    return (
      <Button
        onClick={handleOnClickLogout}
        trackingId={{ eventName: "onboarding.new.exit" }}
        variant={getLogoutButtonVariant()}
        width="sm"
        loading={isPending}
        testId="onboarding-header-trailing-button">
        {t("layouts.onboardingHeader.logout")}
      </Button>
    );
  };

  return (
    <>
      <div className={base({ className: getBackgroundColor() })}>
        {renderProgressBar()}
        <div className={container()}>
          <img
            src={
              hasDarkBackground && !overrideDarkButtons
                ? datashipsLogoLight
                : datashipsLogoDark
            }
            className={logo()}
          />
          <div>{renderTrailingButton()}</div>
        </div>
      </div>
    </>
  );
};

export default OnboardingHeader;
